import { API_URL } from '../utils/helpers';
import { parseQrToken } from '../utils/qrEntry';
import {
  LookupAnswer,
  OrderToken,
  WithdrawalLookup,
  WithdrawalSituation,
} from '../utils/types';

// #70: the order lookup calls of the portal. POST /orders (order number +
// email/phone) and POST /orders/by-qr (a parcel QR token, `rq`) answer with
// the same body, so both continue through the same code in OrderFormPage.
// #154 (S3b): the single entry. The three lookup calls declare the routing
// capability (`X-Portal-Routing: 1`, return-api #408, docs/withdrawal.md
// §2.0): return-api may then answer a WITHDRAWAL session (`purpose`) instead
// of the return one, or 410 WITHDRAWAL_PORTAL_INACTIVE.

export interface OrderLookupParams {
  order_number: string;
  customer_email_or_phone_number: string;
}

export type QrFailure =
  | 'unavailable'
  | 'notReturnable'
  | 'notFulfilled'
  | 'secondReturnNotAllowed'
  | 'alreadyReturned'
  | 'windowPassed'
  | 'deniedByRule'
  // #149 (D5) / #154: a withdrawal-routed QR of a shop whose portal is off
  | 'portalInactive'
  | 'disabledByShop';

export const lookupHeaders = (proof: string | null, shop: string | null) => ({
  'Content-Type': 'application/json',
  'Session-Token': proof ? proof : '',
  Shop: shop ? shop : '',
});

export { parseQrToken };

// #154: added per lookup request (never to lookupHeaders, whose
// `Session-Token: ''` would select the proof handler of the QR redemption)
const PORTAL_ROUTING = { 'X-Portal-Routing': '1' };

export const postOrderLookup = (
  params: OrderLookupParams,
  proof: string | null,
  shop: string | null
): Promise<Response> =>
  fetch(API_URL + '/orders', {
    method: 'POST',
    headers: { ...lookupHeaders(proof, shop), ...PORTAL_ROUTING },
    body: JSON.stringify(params),
  });

export const postOrderByQr = (
  qr: string,
  proof: string,
  shop: string
): Promise<Response> =>
  fetch(API_URL + '/orders/by-qr', {
    method: 'POST',
    headers: { ...lookupHeaders(proof, shop), ...PORTAL_ROUTING },
    body: JSON.stringify({ qr }),
  });

// #119: return-api answers an order that has not shipped yet with 400 and
// this code; every other refusal has no `code` key.
export const ORDER_NOT_FULFILLED = 'ORDER_NOT_FULFILLED';

export const isOrderNotFulfilled = (body: unknown): boolean =>
  !!body &&
  typeof body === 'object' &&
  (body as { code?: unknown }).code === ORDER_NOT_FULFILLED;

// The error body of a failed call, read once; null when it is missing or not
// JSON (the caller then keeps the status-only mapping).
export const errorBodyOf = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

// #149 (D5) / #154: the shop's portal was switched off (410 + this code); on
// the lookup it means a withdrawal-routed answer found the portal inactive
export const isPortalInactive = (status: number, body: unknown): boolean =>
  status === 410 &&
  !!body &&
  typeof body === 'object' &&
  (body as { code?: unknown }).code === 'WITHDRAWAL_PORTAL_INACTIVE';

// A separate terminal outcome: do not conflate merchant mode with portal state.
export const isWithdrawalDisabled = (status: number, body: unknown): boolean =>
  status === 410 &&
  !!body &&
  typeof body === 'object' &&
  (body as { code?: unknown }).code === 'WITHDRAWAL_DISABLED_BY_SHOP';

// #122 (return-api #261): the refusal codes of a lookup / QR redemption (400)
// and of the eligibility call after a manual lookup (`/rules/requirements`,
// 403). Every other 400 / 403 has no `code` key.
export type RefusalCode =
  | 'ORDER_NOT_FULFILLED'
  | 'SECOND_RETURN_NOT_ALLOWED'
  | 'ALREADY_RETURNED'
  | 'RETURN_WINDOW_PASSED'
  | 'RETURN_DENIED_BY_RULE';

const REFUSAL_CODES: readonly string[] = [
  ORDER_NOT_FULFILLED,
  'SECOND_RETURN_NOT_ALLOWED',
  'ALREADY_RETURNED',
  'RETURN_WINDOW_PASSED',
  'RETURN_DENIED_BY_RULE',
];

// The refusal code of a failed call: honoured on a 400 or a 403 only (a 404
// or a 5xx carrying one is still "unavailable"); null for anything else.
export const refusalCodeOf = (
  status: number,
  body: unknown
): RefusalCode | null => {
  if (status !== 400 && status !== 403) return null;
  if (!body || typeof body !== 'object') return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' && REFUSAL_CODES.includes(code)
    ? (code as RefusalCode)
    : null;
};

const QR_FAILURE_BY_CODE: Record<RefusalCode, QrFailure> = {
  ORDER_NOT_FULFILLED: 'notFulfilled',
  SECOND_RETURN_NOT_ALLOWED: 'secondReturnNotAllowed',
  ALREADY_RETURNED: 'alreadyReturned',
  RETURN_WINDOW_PASSED: 'windowPassed',
  RETURN_DENIED_BY_RULE: 'deniedByRule',
};

// 400 is what POST /orders answers for an order with nothing left to return
// (#119 / #122: the reason by its code); every other failure (404
// qr_unavailable included, a 403 without code) means the link is unusable.
export const qrFailureOf = async (response: Response): Promise<QrFailure> => {
  // #154: recognized before the normal refusal mapping
  if (response.status === 410) {
    const body = await errorBodyOf(response);
    if (isWithdrawalDisabled(410, body)) return 'disabledByShop';
    return isPortalInactive(410, body) ? 'portalInactive' : 'unavailable';
  }
  if (response.status !== 400 && response.status !== 403) return 'unavailable';
  const code = refusalCodeOf(response.status, await errorBodyOf(response));
  if (code !== null) return QR_FAILURE_BY_CODE[code];
  return response.status === 400 ? 'notReturnable' : 'unavailable';
};

// #72: the shop verified by return-api for a full-screen QR entry.
export interface VerifiedShop {
  id: number;
  domain: string;
  name: string | null;
}

export interface StandaloneQrOrder extends OrderToken {
  shop: VerifiedShop;
}

// #72: the full-screen entry has no storefront proof and no shop; return-api
// derives the shop from the QR itself.
const postStandaloneEntry = (
  path: string,
  body: Record<string, string>,
  routed = false
): Promise<Response> =>
  fetch(API_URL + path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(routed ? PORTAL_ROUTING : {}),
    },
    body: JSON.stringify(body),
  });

export const postOrderByQrStandalone = (qr: string): Promise<Response> =>
  postStandaloneEntry('/orders/by-qr', { qr }, true);

// C4: hand-off routes on the server without a routing capability header.
export const postOrderByHandoff = (handoff: string): Promise<Response> =>
  postStandaloneEntry('/orders/by-handoff', { handoff });

// A bare host name: the store link is built from it, so no scheme, path,
// port or credentials can slip in.
const SHOP_DOMAIN_PATTERN =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;

// #125: a shop identity { id, domain, name } shared by the by-qr answer and
// the signed status page (shop_id / shop_domain / shop_name of the tracking
// DTO): a positive integer id, a bare host as domain, the name trimmed or
// null. Anything else is no identity at all.
export const shopIdentityOf = (identity: unknown): VerifiedShop | null => {
  const shop = identity as Partial<Record<keyof VerifiedShop, unknown>> | null;
  if (
    !shop ||
    typeof shop.id !== 'number' ||
    !Number.isInteger(shop.id) ||
    shop.id <= 0 ||
    typeof shop.domain !== 'string' ||
    !SHOP_DOMAIN_PATTERN.test(shop.domain)
  ) {
    return null;
  }
  const name = typeof shop.name === 'string' ? shop.name.trim() : '';
  return { id: shop.id, domain: shop.domain, name: name || null };
};

// The shop of a by-qr answer, accepted only when it is the shop of the order
// and the answer carries a client token; anything else is a failed redemption.
export const verifiedShopOf = (body: unknown): VerifiedShop | null => {
  const answer = body as Partial<StandaloneQrOrder> | null;
  if (!answer || typeof answer.token !== 'string' || answer.token === '') {
    return null;
  }
  const shop = shopIdentityOf(answer.shop);
  return shop && shop.id === answer.order_details?.shop_id ? shop : null;
};

// #154: the one test of the response dispatcher: a WITHDRAWAL-routed answer
// carries `purpose: "WITHDRAWAL"`; every other answer is the RETURN body
export const isWithdrawalLookup = (
  answer: LookupAnswer
): answer is WithdrawalLookup =>
  (answer as { purpose?: unknown }).purpose === 'WITHDRAWAL';

const SITUATIONS: readonly string[] = [
  'NOT_SHIPPED',
  'SHIPPED',
  'UNKNOWN',
  'DELIVERED',
];

// #154: the situation preview as the declaration intro reads it; anything
// missing or unknown is the state-neutral UNKNOWN (never a shipping claim)
export const situationOf = (raw: unknown): WithdrawalSituation =>
  typeof raw === 'string' && SITUATIONS.includes(raw)
    ? (raw as WithdrawalSituation)
    : 'UNKNOWN';
